import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

class ReconnectionPolicy {
  nextRetryDelayInMilliseconds() {
    return 10000;
  }
}

const SignalRContext = createContext(undefined);

const MIN_LOG_LEVEL = LogLevel.Debug;
const IGNORED_EVENTS = new Set(["Ping", "ping"]);

// instrumentation for centralized signalr logging
// TODO: move this to better logging package (debug)
const instrumentConnection = (connection) => {
  const originalOn = connection.on.bind(connection);
  const originalOff = connection.off.bind(connection);
  const originalInvoke = connection.invoke.bind(connection);
  const originalSend = connection.send.bind(connection);

  const handlerMap = new Map();
  const tryParse = (value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return value;
      }
    }
    return value;
  };

  const normalizeArgs = (args) => args.map(tryParse);
  const formatArgsForLog = (args) => {
    const parsed = normalizeArgs(args);
    return parsed.length === 1 ? parsed[0] : parsed;
  };

  const getKey = (eventName, callback) => {
    // Use a composite key so same handler can be registered on multiple events
    if (!callback._signalrId) {
      callback._signalrId = Math.random().toString(36).slice(2);
    }
    return `${eventName}::${callback._signalrId}`;
  };

  const dispatchingEvents = new Set();

  connection.on = (eventName, callback) => {
    const key = getKey(eventName, callback);

    if (handlerMap.has(key)) {
      originalOff(eventName, handlerMap.get(key));
      handlerMap.delete(key);
    }

    const wrappedCallback = (...args) => {
      try {
        if (
          !IGNORED_EVENTS.has(eventName) &&
          !dispatchingEvents.has(eventName)
        ) {
          dispatchingEvents.add(eventName);
          console.debug(
            `[SignalR receive] ${eventName}`,
            formatArgsForLog(args)
          );
          Promise.resolve().then(() => dispatchingEvents.delete(eventName));
        }
        return callback(...args);
      } catch (err) {
        console.error(`[SignalR callback:error] ${eventName}`, err, { args });
      }
    };

    handlerMap.set(key, wrappedCallback);
    return originalOn(eventName, wrappedCallback);
  };

  connection.off = (eventName, callback) => {
    const key = getKey(eventName, callback);
    const wrapped = handlerMap.get(key);

    if (wrapped) {
      handlerMap.delete(key);
      const result = originalOff(eventName, wrapped);
      return result;
    }

    return originalOff(eventName, callback);
  };

  connection.invoke = async (methodName, ...args) => {
    if (!IGNORED_EVENTS.has(methodName)) {
      console.debug(`[SignalR invoke] ${methodName}`, formatArgsForLog(args));
    }
    try {
      const result = await originalInvoke(methodName, ...args);
      if (!IGNORED_EVENTS.has(methodName)) {
        console.debug(
          `[SignalR invoke:result] ${methodName}`,
          tryParse(result)
        );
      }
      return result;
    } catch (e) {
      console.error(`[SignalR invoke:error] ${methodName}`, e);
      throw e;
    }
  };

  connection.send = async (methodName, ...args) => {
    if (!IGNORED_EVENTS.has(methodName)) {
      console.debug(`[SignalR send] ${methodName}`, formatArgsForLog(args));
    }
    return originalSend(methodName, ...args);
  };
};

// suppress missing client methods
const customLogger = {
  log: (logLevel, message) => {
    if (logLevel < MIN_LOG_LEVEL) return;
    if (
      logLevel === LogLevel.Warning &&
      message.includes("No client method with the name")
    ) {
      return;
    }
    switch (logLevel) {
      case LogLevel.Error:
        console.error("[SignalR]", message);
        break;
      case LogLevel.Warning:
        console.warn("[SignalR]", message);
        break;
      case LogLevel.Information:
        console.info("[SignalR]", message);
        break;
      case LogLevel.Debug:
        console.debug("[SignalR]", message);
        break;
      default:
        console.log("[SignalR]", message);
        break;
    }
  },
};

export function SignalRProvider({ server, context, tokenFactory, children }) {
  const {
    user: { customerId, groupId, tenantId, userId },
  } = React.useContext(context);

  const [connection, setConnection] = useState(null);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const startingRef = useRef(false);
  const startConnectionRef = useRef(null);

  const authRef = useRef({ customerId, groupId, tenantId, userId });
  useEffect(() => {
    authRef.current = { customerId, groupId, tenantId, userId };
  }, [customerId, groupId, tenantId, userId]);

  useEffect(() => {
    if (!server) return;

    const options = tokenFactory
      ? { accessTokenFactory: async () => await tokenFactory() }
      : undefined;

    const newConnection = new HubConnectionBuilder()
      .withUrl(server, options)
      .withAutomaticReconnect(new ReconnectionPolicy())
      .configureLogging(customLogger)
      .build();

    instrumentConnection(newConnection);

    newConnection.onreconnecting((err) => {
      console.warn("[SignalR] reconnecting", err);
      setError(err);
    });

    newConnection.onreconnected(async () => {
      console.log("[SignalR] reconnected");
      try {
        const { customerId, groupId, tenantId, userId } = authRef.current;
        await newConnection.send("login", customerId, tenantId);
        await newConnection.send(
          "broadcastLogin",
          customerId,
          groupId,
          tenantId,
          userId
        );
        setError(null);
      } catch (e) {
        setError(e);
      }
    });

    newConnection.onclose((err) => {
      console.error("[SignalR] closed", err);
      setError(err);
    });

    setConnection(newConnection);
    window.signalR = newConnection;

    return () => {
      newConnection.stop();
    };
  }, [server, tokenFactory]);

  // Start / restart connection
  const startConnection = useCallback(async () => {
    if (!connection) return;
    if (startingRef.current) return;
    startingRef.current = true;

    try {
      if (connection.state !== HubConnectionState.Disconnected) {
        await connection.stop();
      }

      await connection.start();

      const { customerId, groupId, tenantId, userId } = authRef.current;
      await connection.send("login", customerId, tenantId);
      await connection.send(
        "broadcastLogin",
        customerId,
        groupId,
        tenantId,
        userId
      );

      setError(null);
    } catch (e) {
      console.error("[SignalR] start failed", e);
      setError(e);
      setTimeout(() => startConnectionRef.current?.(), 5000);
    } finally {
      startingRef.current = false;
    }
  }, [connection]);

  useEffect(() => {
    startConnectionRef.current = startConnection;
  }, [startConnection]);

  // Register event handlers
  useEffect(() => {
    if (!connection) return;

    const onLogin = () => setIsLoggedIn(true);
    connection.on("login", onLogin);
    return () => connection.off("login", onLogin);
  }, [connection]);

  // Initial connection
  useEffect(() => {
    if (connection && authRef.current.customerId && authRef.current.tenantId) {
      startConnectionRef.current?.();
    }
  }, [connection]);

  // Monitor for visibility changes
  useEffect(() => {
    if (!connection) return;

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        connection.state !== HubConnectionState.Connected
      ) {
        console.log("[SignalR] App resumed → restarting");
        startConnectionRef.current?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [connection]);

  // Monitor network and recovery
  useEffect(() => {
    if (!connection) return;

    const handleOnline = () => {
      console.log("[SignalR] Network online → restarting");
      startConnectionRef.current?.();
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [connection]);

  return (
    <SignalRContext.Provider
      value={{
        connection,
        isConnected: connection?.state === HubConnectionState.Connected,
        isLoggedIn,
        isError: !!error,
        error,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalR() {
  return useContext(SignalRContext);
}
